import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { UserCheck, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { getRoutingSettings, updateRoutingSettings, type RoutingSettings } from '@/lib/api/routingSettings'
import { AUTO_CLOSE_MAX_MINUTES, INACTIVITY_PRESETS, clampInactivityMinutes } from '@/lib/routing/autoClose'

const CHAT_LIMIT_PRESETS = [2, 3, 5, 8]

export default function RoutingSettingsPanel() {
  const [settings, setSettings] = useState<RoutingSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    getRoutingSettings()
      .then((data) => {
        if (!mounted) return
        setSettings(data)
      })
      .catch((err) => {
        console.error(err)
        toast.error('No se pudieron cargar las configuraciones de enrutamiento')
      })
      .finally(() => setLoading(false))

    return () => {
      mounted = false
    }
  }, [])

  const handleChange = (partial: Partial<RoutingSettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...partial } : prev))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!settings) return

    try {
      setSaving(true)
      const updated = await updateRoutingSettings(settings)
      setSettings(updated)
      toast.success('Configuración de enrutamiento guardada exitosamente')
    } catch (err) {
      console.error(err)
      toast.error('Error al guardar la configuración de enrutamiento')
    } finally {
      setSaving(false)
    }
  }

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-slate-500 font-medium">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#075E54] border-t-transparent" />
          <span>Cargando configuración de enrutamiento...</span>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Tarjeta 1: Auto-asignación */}
      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#075E54] flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Auto-Asignación a Agentes</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Distribuye chats nuevos automáticamente entre los asesores activos del equipo por defecto
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.autoAssignToAgent}
              onChange={(e) => handleChange({ autoAssignToAgent: e.target.checked })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]" />
          </label>
        </div>

        <div className="pt-3 border-t border-slate-100">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Límite de chats abiertos simultáneos por asesor
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="number"
              min={1}
              max={50}
              value={settings.maxOpenConversationsPerAgent}
              onChange={(e) =>
                handleChange({ maxOpenConversationsPerAgent: Number(e.target.value) || 1 })
              }
              className="w-28 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold focus:border-[#075E54] focus:outline-none focus:ring-2 focus:ring-[#075E54]/20 transition"
            />
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-slate-400 mr-1">Rápido:</span>
              {CHAT_LIMIT_PRESETS.map((limit) => (
                <button
                  key={limit}
                  type="button"
                  onClick={() => handleChange({ maxOpenConversationsPerAgent: limit })}
                  className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${
                    settings.maxOpenConversationsPerAgent === limit
                      ? 'bg-[#075f55] text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {limit} chats
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Un asesor no recibirá nuevas conversaciones si ya alcanzó este límite de chats abiertos.
          </p>
        </div>
      </div>

      {/* Tarjeta 2: Cierre automático */}
      <div className="rounded-2xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#075E54] flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Cierre Automático por Inactividad</h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Cierra y archiva chats automáticamente cuando el cliente no responde tras un período determinado
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings.autoCloseEnabled}
              onChange={(e) => handleChange({ autoCloseEnabled: e.target.checked })}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]" />
          </label>
        </div>

        <div className="pt-3 border-t border-slate-100">
          <label htmlFor="auto-close-minutes" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Tiempo de inactividad (minutos)
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="number"
              min={5}
              id="auto-close-minutes"
              max={AUTO_CLOSE_MAX_MINUTES}
              disabled={!settings.autoCloseEnabled}
              value={settings.autoCloseInactivityMinutes}
              onChange={(e) =>
                handleChange({
                  autoCloseInactivityMinutes: clampInactivityMinutes(Number(e.target.value)),
                })
              }
              className="w-32 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold focus:border-[#075E54] focus:outline-none focus:ring-2 focus:ring-[#075E54]/20 disabled:bg-slate-50 disabled:text-slate-400 transition"
            />
            {settings.autoCloseEnabled && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-slate-400 mr-1">Preajustes:</span>
                {INACTIVITY_PRESETS.map((preset) => (
                  <button
                    key={preset.minutes}
                    type="button"
                    onClick={() => handleChange({ autoCloseInactivityMinutes: preset.minutes })}
                    className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-all ${
                      settings.autoCloseInactivityMinutes === preset.minutes
                        ? 'bg-[#075f55] text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Mínimo 5 minutos, máximo 48 horas (2,880 minutos), que es también el valor por defecto. El siguiente mensaje del
            cliente abre una conversación nueva.
          </p>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#075E54] hover:bg-[#064E46] text-white font-semibold rounded-xl shadow-sm px-6 py-2.5"
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </div>
    </form>
  )
}
